import {
  AdjustmentsHorizontalIcon,
  ChatBubbleLeftRightIcon,
  InboxArrowDownIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

import { useSettingsCompleteness } from '../hooks/useSettingsCompleteness';

import UserMenu from './UserMenu/UserMenu';

interface SidebarProps {
  user: {
    picture?: string;
    name?: string;
    email?: string;
  };
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ user, isOpen, onToggle }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { hasIncompleteSettings } = useSettingsCompleteness();

  const navItems = [
    {
      to: '/conversation',
      icon: ChatBubbleLeftRightIcon,
      label: 'Conversations',
    },
    {
      to: '/bulk-upload',
      icon: InboxArrowDownIcon,
      label: 'Bulk Upload',
    },
    {
      to: '/booking-ruleset',
      icon: AdjustmentsHorizontalIcon,
      label: 'Booking Rules',
    },
    {
      to: '/my-settings',
      icon: Cog6ToothIcon,
      label: 'Settings',
    },
  ];

  return (
    <div
      className={`${
        isOpen ? 'w-64' : 'w-20'
      } h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300 ease-in-out relative z-20`}
    >
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className='absolute -right-3 top-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200'
        aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        {isOpen ? (
          <ChevronLeftIcon className='w-4 h-4 text-gray-600 dark:text-gray-400' />
        ) : (
          <ChevronRightIcon className='w-4 h-4 text-gray-600 dark:text-gray-400' />
        )}
      </button>

      {/* Logo Section */}
      <div className='p-4 flex items-center justify-center border-b border-gray-200 dark:border-gray-700'>
        <NavLink to='/conversation' className='flex items-center'>
          <img
            src='/assets/images/img_image_2.png'
            alt='Acconto.ai Logo'
            className={`${isOpen ? 'h-20' : 'h-16'} transition-all duration-300`}
          />
        </NavLink>
      </div>

      {/* Navigation Section */}
      <nav className='flex-1 p-4 space-y-2 overflow-y-auto'>
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center ${
                isOpen ? 'px-4' : 'px-2 justify-center'
              } py-3 rounded-lg transition-colors duration-200 relative ${
                isActive
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`
            }
          >
            <Icon className={`w-6 h-6 flex-shrink-0 ${isOpen ? 'mr-3' : ''}`} />
            {isOpen && (
              <>
                <span className='font-medium text-sm whitespace-nowrap'>
                  {label}
                </span>
                {to === '/my-settings' && hasIncompleteSettings && (
                  <ExclamationCircleIcon
                    className='w-6 h-6 text-amber-500 dark:text-amber-400 ml-auto flex-shrink-0'
                    aria-label='Incomplete settings'
                  />
                )}
              </>
            )}
            {!isOpen && to === '/my-settings' && hasIncompleteSettings && (
              <ExclamationCircleIcon
                className='w-5 h-5 text-amber-500 dark:text-amber-400 absolute top-0.5 right-0.5'
                aria-label='Incomplete settings'
              />
            )}
          </NavLink>
        ))}
      </nav>

      {/* User Info Section */}
      <div className='border-t border-gray-200 dark:border-gray-700 p-4'>
        <div className='relative'>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className={`flex items-center w-full ${
              isOpen ? 'px-3' : 'justify-center'
            } py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200`}
          >
            <img
              src={user.picture || '/assets/images/default-avatar.png'}
              alt={user.name || 'User'}
              className={`${
                isOpen ? 'w-10 h-10' : 'w-8 h-8'
              } rounded-full object-cover flex-shrink-0`}
            />
            {isOpen && (
              <div className='ml-3 text-left flex-1 min-w-0'>
                <p className='text-sm font-medium text-gray-900 dark:text-gray-100 truncate'>
                  {user.name || 'User'}
                </p>
                <p className='text-xs text-gray-500 dark:text-gray-400 truncate'>
                  {user.email || ''}
                </p>
              </div>
            )}
          </button>

          {/* User Menu Dropdown */}
          {showUserMenu && isOpen && (
            <>
              <div
                className='fixed inset-0 z-10'
                onClick={() => setShowUserMenu(false)}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    setShowUserMenu(false);
                  }
                }}
                role='button'
                tabIndex={0}
                aria-label='Close user menu'
              />
              <div className='absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 p-2'>
                <UserMenu />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
